/**
 * Organization Members Hooks
 * React Query hooks for managing organization members
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { EDGE_FUNCTION_HEADERS } from '@/lib/edge-functions';
import { supabase } from '@/lib/supabase';

const API_BASE = import.meta.env.VITE_SUPABASE_URL;

interface Member {
  id: string;
  user_id: string;
  role: string;
  status: 'active' | 'suspended';
  joined_at: string;
  created_at: string;
  updated_at: string;
  email: string | null;
  global_role: 'super_admin' | 'admin' | 'user' | null;
  /**
   * Holds the org-scoped financials[read] grant (Finance visibility for a
   * plain member — owners/admins see Finance implicitly regardless).
   */
  finance_access?: boolean;
  user_profiles: {
    id: string;
    full_name: string | null;
    avatar_url: string | null;
  } | null;
}

interface MembersResponse {
  members: Member[];
}

interface UpdateMemberStatusInput {
  memberId: string;
  status: 'active' | 'suspended';
}

interface UpdateMemberRoleInput {
  memberId: string;
  role: 'admin' | 'member' | 'viewer' | 'advisor';
}

// Helper to safely extract error message from a response
async function getErrorMessage(response: Response, fallback: string): Promise<string> {
  try {
    const data = await response.json();
    return data.error || fallback;
  } catch {
    return `${fallback} (HTTP ${response.status})`;
  }
}

// Helper to get auth token — throws if not authenticated
async function getAuthToken(): Promise<string> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) throw new Error('Not authenticated');
  return session.access_token;
}

// Fetch members for an organization
export function useOrganizationMembers(organizationId: string | null) {
  return useQuery({
    queryKey: ['organization-members', organizationId],
    queryFn: async (): Promise<MembersResponse> => {
      if (!organizationId) throw new Error('Organization ID required');

      const token = await getAuthToken();
      const response = await fetch(
        `${API_BASE}/functions/v1/bss-organizations-members?organizationId=${organizationId}`,
        {
          headers: {
            ...EDGE_FUNCTION_HEADERS,
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error(await getErrorMessage(response, 'Failed to fetch members'));
      }

      return response.json();
    },
    enabled: !!organizationId,
    refetchOnMount: 'always', // Always refetch when component mounts, even with cached data
    staleTime: 30000, // 30 seconds
  });
}

// Pause or activate a member
export function useUpdateMemberStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ memberId, status }: UpdateMemberStatusInput) => {
      const token = await getAuthToken();
      const response = await fetch(
        `${API_BASE}/functions/v1/bss-organizations-members/${memberId}`,
        {
          method: 'PATCH',
          headers: {
            ...EDGE_FUNCTION_HEADERS,
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ status }),
        }
      );

      if (!response.ok) {
        throw new Error(await getErrorMessage(response, 'Failed to update member status'));
      }

      return response.json();
    },
    onSuccess: () => {
      // Invalidate all member queries
      queryClient.invalidateQueries({
        queryKey: ['organization-members']
      });
    },
  });
}

// Set / unset a member's Finance access (the financials[read] grant).
// Owner/admin only — enforced server-side; mirrors the useUpdateMemberStatus shape.
export function useUpdateMemberFinanceAccess() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      { memberId, financeAccess }: { memberId: string; financeAccess: boolean },
    ) => {
      const token = await getAuthToken();
      const response = await fetch(
        `${API_BASE}/functions/v1/bss-organizations-members/${memberId}`,
        {
          method: 'PATCH',
          headers: {
            ...EDGE_FUNCTION_HEADERS,
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ financeAccess }),
        }
      );

      if (!response.ok) {
        throw new Error(await getErrorMessage(response, 'Failed to update finance access'));
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organization-members'] });
      // The gate itself reads this — a freshly (un)granted member must re-resolve.
      queryClient.invalidateQueries({ queryKey: ['finance-access'] });
    },
  });
}

// Remove a member
export function useRemoveMember() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (memberId: string) => {
      const token = await getAuthToken();
      const response = await fetch(
        `${API_BASE}/functions/v1/bss-organizations-members/${memberId}`,
        {
          method: 'DELETE',
          headers: {
            ...EDGE_FUNCTION_HEADERS,
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error(await getErrorMessage(response, 'Failed to remove member'));
      }

      return response.json();
    },
    onSuccess: () => {
      // Invalidate all member queries
      queryClient.invalidateQueries({
        queryKey: ['organization-members']
      });
    },
  });
}

// Update a member's role
export function useUpdateMemberRole() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ memberId, role }: UpdateMemberRoleInput) => {
      const token = await getAuthToken();
      const response = await fetch(
        `${API_BASE}/functions/v1/bss-organizations-members/${memberId}`,
        {
          method: 'PATCH',
          headers: {
            ...EDGE_FUNCTION_HEADERS,
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ role }),
        }
      );

      if (!response.ok) {
        throw new Error(await getErrorMessage(response, 'Failed to update member role'));
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['organization-members']
      });
    },
  });
}

// Export types
export type { Member, MembersResponse, UpdateMemberStatusInput, UpdateMemberRoleInput };
